import { useCallback, useEffect, useState } from 'react';
import { apiRequest, ApiError } from './api';

export interface Query<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  /** Re-runs the request. Wired to the retry control every failed band shows. */
  reload: () => void;
}

/**
 * A GET with the three states every screen must handle (Stage 1B 5.4): loading,
 * error-with-retry, and data. Aborts in flight on unmount or dependency change,
 * so a fast navigator never sees a resolved response land on a dead screen.
 */
export function useApi<T = unknown>(
  path: string | null,
  query?: Record<string, string | number | boolean | undefined>,
): Query<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError>();
  const [loading, setLoading] = useState<boolean>(!!path);
  const [nonce, setNonce] = useState(0);

  const key = query ? JSON.stringify(query) : '';

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    apiRequest<T>(path, { query, signal: controller.signal })
      .then((res) => {
        if (!controller.signal.aborted) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err : new ApiError(0, 'network', 'Could not reach the server.'));
        setLoading(false);
      });
    return () => controller.abort();
    // key captures the query; nonce forces a manual reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}
